/**
 * Lightweight animation utility using Web Animations API.
 * API-compatible with Motion One for consistency across the codebase.
 */
const Motion = {
  animate(elements, keyframes, options = {}) {
    const elementArray = elements instanceof NodeList
      ? Array.from(elements)
      : Array.isArray(elements)
        ? elements
        : [elements];

    const animations = elementArray.map((element, i) => {
      const processedKeyframes = {};
      if (typeof keyframes === 'object' && !Array.isArray(keyframes)) {
        Object.keys(keyframes).forEach((property) => {
          const values = keyframes[property];
          processedKeyframes[property] = Array.isArray(values) ? values : [values, values];
        });
      }

      const delay = typeof options.delay === 'function'
        ? options.delay(i, elementArray.length)
        : (options.delay || 0);

      const processedOptions = {
        duration: (options.duration || 0.3) * 1000,
        delay: delay * 1000,
        easing: Motion.convertEasing(options.easing || 'ease'),
        fill: options.fill || 'both',
      };

      return element.animate(processedKeyframes, processedOptions);
    });

    return {
      finished: Promise.all(animations.map((anim) => anim.finished)),
    };
  },

  stagger(increment) {
    return (i) => i * increment;
  },

  convertEasing(easing) {
    if (Array.isArray(easing)) {
      return `cubic-bezier(${easing.join(', ')})`;
    }
    return easing;
  },
};

/**
 * <mega-menu-custom> web component.
 * Handles hover/focus-driven panel switching for the custom mega menu layout.
 * Level-2 category links in the left column control which level-3 panel is visible.
 */
class MegaMenuCustom extends HTMLElement {
  constructor() {
    super();
    this.activeIndex = -1;
    this.hoverTimeout = null;
  }

  connectedCallback() {
    this.categoryLinks = this.querySelectorAll('.mega-menu-custom__category-link');
    this.panels = this.querySelectorAll('.mega-menu-custom__panel');

    this.categoryLinks.forEach((link) => {
      link.addEventListener('mouseenter', this.onHover.bind(this));
      link.addEventListener('focusin', this.onFocus.bind(this));
    });

    // Find the first category that has a panel and activate it
    this.activateFirstAvailablePanel();

    // Re-activate first panel each time the mega menu opens
    this.observeOpen();
  }

  observeOpen() {
    const details = this.closest('details.mega-menu');
    if (!details) return;

    this.mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.attributeName === 'open' && details.hasAttribute('open')) {
          this.activateFirstAvailablePanel();
        }
      }
    });
    this.mutationObserver.observe(details, { attributes: true });
  }

  disconnectedCallback() {
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
  }

  activateFirstAvailablePanel() {
    for (let i = 0; i < this.categoryLinks.length; i++) {
      const panelIndex = this.categoryLinks[i].dataset.panelIndex;
      const panels = this.querySelectorAll(`.mega-menu-custom__panel[data-panel-index="${panelIndex}"]`);
      if (panels.length > 0) {
        this.activatePanel(panelIndex, true);
        return;
      }
    }
  }

  onHover(event) {
    clearTimeout(this.hoverTimeout);
    const index = event.currentTarget.dataset.panelIndex;
    // Small debounce to prevent flickering when moving between items
    this.hoverTimeout = setTimeout(() => this.activatePanel(index), 40);
  }

  onFocus(event) {
    clearTimeout(this.hoverTimeout);
    const index = event.currentTarget.dataset.panelIndex;
    this.activatePanel(index);
  }

  activatePanel(index, instant = false) {
    if (index === this.activeIndex) return;

    const previousPanels = this.querySelectorAll('.mega-menu-custom__panel.is-active');
    const nextPanels = this.querySelectorAll(`.mega-menu-custom__panel[data-panel-index="${index}"]`);

    // Update active state on category links
    this.categoryLinks.forEach((l) => l.classList.remove('is-active'));
    const activeLink = this.querySelector(`.mega-menu-custom__category-link[data-panel-index="${index}"]`);
    if (activeLink) activeLink.classList.add('is-active');

    // Deactivate all previously active panels
    previousPanels.forEach((panel) => {
      panel.classList.remove('is-active');
    });

    // Activate all matching panels (zone 2 + zone 3)
    nextPanels.forEach((panel) => {
      if (!instant && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        Motion.animate(panel, {
          opacity: [0, 1],
        }, {
          duration: 0.2,
          easing: [0.25, 0.1, 0.25, 1],
        });
      }
      panel.classList.add('is-active');
    });

    this.activeIndex = index;
  }
}

customElements.define('mega-menu-custom', MegaMenuCustom);

/**
 * <details-mega> web component.
 * Wraps a native <details> element to provide animated open/close
 * with hover and click trigger support for mega menu dropdowns.
 * Replaces Dawn's <header-menu> for mega menu mode.
 */
class DetailsMega extends HTMLElement {
  constructor() {
    super();
    this.detailsElement = this.querySelector('details');
    this.summaryElement = this.detailsElement?.querySelector('summary');
    this.contentElement = this.summaryElement?.nextElementSibling;
    this._open = false;
    this._transitioning = false;
    this.hoverTimer = null;
    this.header = document.querySelector('.header-wrapper');

    this.summaryElement?.addEventListener('click', this.handleSummaryClick.bind(this));

    if (this.trigger === 'hover') {
      this.summaryElement?.addEventListener('focusin', (event) => {
        if (event.target === this.summaryElement) this.open = true;
      });
    }

    this.handleOutsideClick = this.handleOutsideClick.bind(this);
    this.handleEscKeyPress = this.handleEscKeyPress.bind(this);
    this.handleFocusOut = this.handleFocusOut.bind(this);
    this.detectHover = this.detectHover.bind(this);

    this.addEventListener('mouseenter', this.detectHover);
    this.addEventListener('mouseleave', this.detectHover);

    if (typeof Shopify !== 'undefined' && Shopify.designMode) {
      this.addEventListener('shopify:block:select', () => { this.open = true; });
      this.addEventListener('shopify:block:deselect', () => { this.open = false; });
    }
  }

  get trigger() {
    if ('ontouchstart' in window || navigator.maxTouchPoints > 0) return 'click';
    return this.getAttribute('trigger') || 'click';
  }

  set open(value) {
    if (value !== this._open) {
      this._open = value;
      this.transition(value);
    }
  }

  get open() {
    return this._open;
  }

  handleSummaryClick(event) {
    event.preventDefault();
    if (this.trigger === 'hover' && this.summaryElement.hasAttribute('data-link')) {
      window.location.href = this.summaryElement.getAttribute('data-link');
    } else {
      this.open = !this.open;
    }
  }

  async transition(value) {
    if (this._transitioning) return;
    this._transitioning = true;

    if (value) {
      // Close any other open mega menus
      document.querySelectorAll('details-mega').forEach((other) => {
        if (other !== this && other._open) {
          other._open = false;
          other.detailsElement.removeAttribute('open');
          other.summaryElement?.setAttribute('aria-expanded', 'false');
          other.removeListeners();
        }
      });

      if (this.header) {
        this.header.preventHide = true;
        if (!document.documentElement.style.getPropertyValue('--header-bottom-position-desktop')) {
          document.documentElement.style.setProperty(
            '--header-bottom-position-desktop',
            `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
          );
        }
      }

      this.detailsElement.setAttribute('open', '');
      this.summaryElement?.setAttribute('aria-expanded', 'true');

      // Cancel any lingering CSS animations Dawn may have started
      this.contentElement?.getAnimations().forEach((a) => a.cancel());

      this.addListeners();
      await this.showWithTransition();
    } else {
      await this.hideWithTransition();

      this.detailsElement.removeAttribute('open');
      this.summaryElement?.setAttribute('aria-expanded', 'false');
      if (this.header) this.header.preventHide = false;

      this.removeListeners();
    }

    this._transitioning = false;
  }

  async showWithTransition() {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    return Motion.animate(this.contentElement, {
      opacity: [0, 1],
      transform: ['translateY(-1rem)', 'translateY(0)'],
    }, {
      duration: reduced ? 0 : 0.4,
      easing: [0.39, 0.575, 0.565, 1.0],
    }).finished;
  }

  async hideWithTransition() {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    return Motion.animate(this.contentElement, {
      opacity: [1, 0],
      transform: ['translateY(0)', 'translateY(-1rem)'],
    }, {
      duration: reduced ? 0 : 0.3,
      easing: [0.39, 0.575, 0.565, 1.0],
    }).finished;
  }

  addListeners() {
    document.addEventListener('click', this.handleOutsideClick);
    document.addEventListener('keydown', this.handleEscKeyPress);
    this.addEventListener('focusout', this.handleFocusOut);
  }

  removeListeners() {
    document.removeEventListener('click', this.handleOutsideClick);
    document.removeEventListener('keydown', this.handleEscKeyPress);
    this.removeEventListener('focusout', this.handleFocusOut);
  }

  handleOutsideClick(event) {
    if (!this.contains(event.target)) {
      this.open = false;
    }
  }

  handleEscKeyPress(event) {
    if (event.key === 'Escape') {
      this.open = false;
      this.summaryElement?.focus();
    }
  }

  handleFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        this.open = false;
      }
    });
  }

  detectHover(event) {
    if (this.trigger !== 'hover') return;
    clearTimeout(this.hoverTimer);
    if (event.type === 'mouseenter') {
      this.hoverTimer = setTimeout(() => { this.open = true; }, 80);
    } else {
      this.hoverTimer = setTimeout(() => { this.open = false; }, 150);
    }
  }

  close() {
    this.open = false;
  }
}

customElements.define('details-mega', DetailsMega);
